import GuideLayout from '@/pages/shared/GuideLayout';

export default function GuideWBSGantt() {
  return (
    <GuideLayout
      title="WBSとガントチャートの違い｜プロジェクト管理の基本用語解説｜”おもいむすび”"
      description="WBSとガントチャートの違いを徹底解説。WBSは作業の構造化、ガントチャートはスケジュールの可視化。両者の役割の違いと、プロジェクト管理における使い分け方を解説します。"
      keywords="WBS ガントチャート 違い,WBSとは ガントチャートとは,プロジェクト管理 WBS,プロジェクト管理 ガントチャート"
      canonical="https://omoimusubi.asia/guides/wbs-and-gantt-chart/"
      h1="WBSとガントチャートの違いを徹底解説"
      subtitle="プロジェクト管理の基本ツールであるWBSとガントチャート。それぞれの役割と違いを理解し、プロジェクト管理に活かす方法を解説します。"
      relatedFeatures={[
        { label: 'AIタスク分解', path: '/features/ai-task-breakdown/' },
        { label: 'AIガントチャート', path: '/features/ai-gantt/' },
      ]}
      relatedGuides={[
        { label: 'WBSとは', path: '/guides/wbs/' },
        { label: 'ガントチャートとは', path: '/guides/gantt-chart/' },
        { label: 'WBSテンプレート', path: '/templates/wbs/' },
        { label: 'プロジェクト管理とは', path: '/guides/project-management/' },
      ]}
    >
      <section>
        <h2>WBSとガントチャートの本質的な違い</h2>
        <p>WBSは「What（何をするか）」を整理するツール、ガントチャートは「When（いつやるか）」を整理するツールです。両者は目的が異なり、プロジェクト管理では両方を組み合わせて使うことが効果的です。</p>
      </section>

      <div className="not-prose my-6 overflow-x-auto">
        <h3 className="text-base font-bold text-foreground-900 mb-3">WBSとガントチャートの比較</h3>
        <table className="min-w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-background-300">
              <th className="text-left p-3 font-bold text-foreground-900 bg-background-100 min-w-[140px]">比較項目</th>
              <th className="text-left p-3 font-bold text-foreground-900 bg-background-100 min-w-[220px]">WBS</th>
              <th className="text-left p-3 font-bold text-foreground-900 bg-background-100 min-w-[220px]">ガントチャート</th>
            </tr>
          </thead>
          <tbody className="text-foreground-600">
            {[
              { label: '目的', wbs: '作業の洗い出しと構造化', gantt: 'スケジュールの可視化と管理' },
              { label: '表現するもの', wbs: '「何を」作るか・行うか', gantt: '「いつ」行うか' },
              { label: '形式', wbs: '階層構造（ツリー図）', gantt: '時系列の棒グラフ' },
              { label: '主な構成要素', wbs: '成果物、作業パッケージ', gantt: 'タスクバー、依存関係矢印、マイルストーン' },
              { label: '時間軸', wbs: 'なし', gantt: 'あり（横軸が日付）' },
              { label: '作成タイミング', wbs: 'プロジェクト計画の初期', gantt: 'WBS作成後' },
              { label: '管理の焦点', wbs: '作業の抜け漏れ防止', gantt: '進捗と遅延の管理' },
            ].map((row) => (
              <tr key={row.label} className="border-b border-background-200/70 hover:bg-background-100/50">
                <td className="p-3 font-medium text-foreground-800">{row.label}</td>
                <td className="p-3">{row.wbs}</td>
                <td className="p-3">{row.gantt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <section>
        <h2>WBSとガントチャートの関係</h2>
        <p>プロジェクト管理の標準的な流れでは、まずWBSを作成して必要な作業をすべて洗い出し、その後に各作業をガントチャート上に時系列で配置します。WBSで作業の抜け漏れを防ぎ、ガントチャートでスケジュールを管理する、という役割分担です。</p>
        <p>”おもいむすび”では、AIが資料からWBSを自動生成し、その内容をもとにガントチャートも自動で作成します。WBSとガントチャートの両方を、一つのツール上でシームレスに管理できます。</p>
      </section>

      <section>
        <h2>よくある誤解</h2>
        <h3>「WBSとガントチャートは同じもの？」</h3>
        <p>よくある誤解として、WBSとガントチャートを同一視してしまうケースがあります。しかし、WBSはタスクを構造化した一覧であり、時間軸は含まれていません。一方ガントチャートは時間軸が中心です。両者は補完関係にあり、どちらか一方だけでプロジェクト管理をするのは不十分です。</p>
        <h3>「WBSを作らなくてもガントチャートだけで十分？」</h3>
        <p>ガントチャートから直接タスクを入力することも可能ですが、WBSを先に作ることで作業の抜け漏れを防ぎ、タスクの階層構造を明確にできます。特に中規模以上のプロジェクトでは、WBSの作成がプロジェクト成功の鍵となります。</p>
      </section>

      <section>
        <h2>AIを活用したWBSとガントチャートの同時作成</h2>
        <p>”おもいむすび”のAIタスク分解機能では、企画書や提案書をアップロードするだけで、AIがWBSを自動生成し、同時にガントチャートも作成します。タスクの階層構造、依存関係、マイルストーン、所要期間をAIが提案するため、WBS作成とガントチャート作成の両方の手間を大幅に削減できます。</p>
      </section>
    </GuideLayout>
  );
}